import { Box, Modal } from "@mui/material";
import { UseQueryResult } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import React, { useState } from "react";
import Loader from "../../components/Loader";
import { AppointmentCard } from "./appointmentCard"; // Import the new component
import CancelAppointmentDialog from "./cancelAppointmentDialog"; // Import the dialog
import { LoadingModal } from "../../components/Modal"; // Import your custom loading modal

// Define the props interface
interface CurrentAppointmentsTabProps {
  currentAppointmentsObject: UseQueryResult<AxiosResponse<any, any>, Error>;
  accessToken: string;
}

const CurrentAppointmentsTab: React.FC<CurrentAppointmentsTabProps> = ({
  currentAppointmentsObject,
  accessToken,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false); // Loading state
  const [showSuccessModal, setShowSuccessModal] = useState(false); // Success modal state
  const [showErrorModal, setShowErrorModal] = useState(false); // Error modal state

  const handleOpenDialog = (appointmentId: string) => {
    setSelectedAppointmentId(appointmentId);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedAppointmentId(null);
  };

  const handleCancelSuccess = () => {
    currentAppointmentsObject.refetch(); // Refetch the appointments after cancellation
    setShowSuccessModal(true); // Show success modal
    setIsLoading(false); // Reset loading state
  };

  const handleCancelError = () => {
    setShowErrorModal(true); // Show error modal
    setIsLoading(false); // Reset loading state
  };

  if (currentAppointmentsObject.isLoading) {
    return (
      <div className="h-[70vh] flex flex-row justify-center items-center w-full">
        <Loader />
      </div>
    );
  }

  if (currentAppointmentsObject.error) {
    return <div>Error: {currentAppointmentsObject.error.message}</div>;
  }

  // Safe check for data and appointments
  const appointments = currentAppointmentsObject.data?.data?.appointments || [];
  const patientId = currentAppointmentsObject.data?.data?.appointments?.[0]?.patientId;

  // Filter appointments to show only past appointments that are NOT cancelled
  const pastAppointments = appointments.filter(
    (visit: any) => {
      // Parse the date more explicitly (format: "M/D/YYYY H:mm:ss AM/PM")
      // Example: "7/31/2024 9:30:00 AM"
      const appointmentDate = new Date(visit.appointmentDate);

      // Get current date and time
      const now = new Date();

      // For debugging - log a few examples
      if (appointments.indexOf(visit) < 3) {
        console.log(`\n=== Appointment Filtering Debug ===`);
        console.log(`Appointment ${visit.appointmentId}:`);
        console.log(`  Original date string: "${visit.appointmentDate}"`);
        console.log(`  Parsed appointment date: ${appointmentDate}`);
        console.log(`  Appointment timestamp: ${appointmentDate.getTime()}`);
        console.log(`  Current date: ${now}`);
        console.log(`  Current timestamp: ${now.getTime()}`);
        console.log(`  Difference (ms): ${now.getTime() - appointmentDate.getTime()}`);
        console.log(`  Is appointment in past? ${appointmentDate < now}`);
        console.log(`  Is valid date? ${!isNaN(appointmentDate.getTime())}`);
        console.log(`  Status: ${visit.status}`);
        console.log(`  Is cancelled? ${visit.status === "canceled"}`);
        console.log(`================================\n`);
      }

      // Only show appointments that are:
      // 1. Strictly in the past
      // 2. Have a valid date
      // 3. Have a valid status that indicates the appointment is active/assigned
      return appointmentDate < now &&
        !isNaN(appointmentDate.getTime()) &&
        (visit.status === "assigned" || visit.status === "Attended/Undischarged");
    }
  );

  return (
    <div className="md:fixed mt-3 flex justify-center">
      <div className="relative w-full h-[70vh] overflow-y-scroll">
        <div className="justify-start flex flex-col flex-wrap md:grid items-start grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 w-full relative max-h-[95%] mb-3 md:mb-0 overflow-y-scroll pb-2">
          {pastAppointments.length > 0 ? (
            pastAppointments.map((visit: any) => (
              <AppointmentCard
                key={visit.appointmentId}
                image={visit.image}
                specialistName={visit.specialistName}
                appointmentName={visit.appointmentName}
                appointmentCategoryTypeName={visit.appointmentCategoryTypeName}
                appointmentDate={visit.appointmentDate}
                appointStartTimeName={visit.appointStartTimeName}
                branchName={visit.branchName}
                status={visit.status}
                appointmentId={visit.appointmentId}
                accessToken={accessToken}
                healthFacilityId={visit.healthFacilityId}
                patientId={visit.patientId}
                onCancelClick={() => handleOpenDialog(visit.appointmentId)}
              />
            ))
          ) : (
            <div className="flex w-full justify-center">
              <p className="text-red-500 text-base">
                No current appointments available
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Only render dialog if we have a patientId */}
      {patientId && (
        <CancelAppointmentDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          appointmentId={selectedAppointmentId!}
          accessToken={accessToken}
          onCancelSuccess={handleCancelSuccess}
          onCancelError={handleCancelError}
          healthFacilityId={"1"}
          patientId={patientId}
          setIsLoading={setIsLoading}
        />
      )}

      {/* Custom Loading Modal */}
      <LoadingModal open={isLoading} isLoading={isLoading} />

      {/* Success Modal */}
      <Modal open={showSuccessModal} onClose={() => setShowSuccessModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            textAlign: "center",
            bgcolor: "background.paper",
            border: "2px solid #000",
            boxShadow: 24,
            p: 4,
          }}
          className="md:w-[400px] w-10/12"
        >
          <h3 className="text-center">Appointment Cancelled</h3>
          <p className="py-3">Your appointment has been successfully canceled.</p>
          <button onClick={() => setShowSuccessModal(false)} className="bg-primary-100 hover:opacity-70 text-white py-2 px-8 rounded-3xl transition-all duration-300 ease-in-out">
            Done
          </button>
        </Box>
      </Modal>

      {/* Error Modal */}
      <Modal open={showErrorModal} onClose={() => setShowErrorModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            textAlign: "center",
            bgcolor: "background.paper",
            border: "2px solid #000",
            boxShadow: 24,
            p: 4,
          }}
          className="md:w-[400px] w-10/12"
        >
          <h3 className="text-center">Error</h3>
          <p className="py-3">Error canceling appointment. Please try again later.</p>
          <button onClick={() => setShowErrorModal(false)} className="bg-primary-100 hover:opacity-70 text-white py-2 px-8 rounded-3xl transition-all duration-300 ease-in-out">
            Close
          </button>
        </Box>
      </Modal>
    </div>
  );
};

export default CurrentAppointmentsTab;